import { Button } from "@/components/ui/UIElements/Button";
import React, { useState } from "react";
import { Modal, Icon } from "../../";
import { LoadingState, ErrorState } from "../../States";
import { useIntegrationLogs, useIntegrationLogDetail } from "../../../../hooks";
import { Pagination } from "../../Data";

const IntegrationLogs = ({ model, modelId, pluginId, isOpen, onClose }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedLog, setSelectedLog] = useState(null);
  const [showLogDetails, setShowLogDetails] = useState(false);
  const [relatedGroupEvent, setRelatedGroupEvent] = useState(null);
  const [showAllTrace, setShowAllTrace] = useState(false);

  const pageSize = 10;

  // Fetch logs list using custom hook
  const {
    data: logsData,
    isLoading,
    error,
  } = useIntegrationLogs({
    model,
    modelId,
    pluginId,
    page: currentPage,
    pageSize,
    relatedGroupEvent,
    enabled: isOpen,
  });

  // Fetch individual log details using custom hook
  const { data: logDetail, isLoading: isLoadingDetail } =
    useIntegrationLogDetail(selectedLog?.id, showLogDetails);

  const logs = logsData?.logs || [];
  const pagination = logsData?.pagination || {};

  const handleLogSelect = (log) => {
    setSelectedLog(log);
    setShowLogDetails(true);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleClose = () => {
    setShowLogDetails(false);
    setSelectedLog(null);
    onClose();
  };

  const handleViewRelatedEvents = (groupKey) => {
    setRelatedGroupEvent(groupKey);
    setCurrentPage(1);
  };

  const clearRelatedEvents = () => {
    setRelatedGroupEvent(null);
    setCurrentPage(1);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleString();
  };

  const getLogTypeColor = (type) => {
    switch (type) {
      case "info":
        return "#2C7BE5";
      case "warning":
        return "#FFC700";
      case "critical":
        return "#E63757";
      default:
        return "#6B7280";
    }
  };

  const getLogTypeLabel = (type) => {
    switch (type) {
      case "info":
        return "Info";
      case "warning":
        return "Warning";
      case "critical":
        return "Critical";
      default:
        return "Unknown";
    }
  };

  const renderDynamicData = (data, excludeKeys = []) => {
    const renderItem = (key, value) => {
      if (excludeKeys.includes(key) || value == null) {
        return null;
      }

      if (key === "execution_time") {
        value = `${value} ms`;
      }

      if (Array.isArray(value) && key === "trace") {
        return (
          <div key={key} className="mb-4">
            <strong className="text-white">{key}:</strong>
            <ul className="mt-2 space-y-1">
              {(showAllTrace ? value : value.slice(0, 6)).map((item, index) => (
                <li key={index} className="text-foreground text-sm">
                  {item}
                </li>
              ))}
              {value.length > 6 && (
                <li>
                  <Button
                    variant="ghost"
                    onClick={() => setShowAllTrace((prev) => !prev)}
                    className="text-primary hover:text-primary text-sm underline"
                  >
                    {showAllTrace
                      ? "Show less"
                      : `Show more (${value.length - 6} more)`}
                  </Button>
                </li>
              )}
            </ul>
          </div>
        );
      }

      if (typeof value === "object" && value !== null) {
        return (
          <div key={key} className="mb-4">
            <strong className="text-white">{key}:</strong>
            <ul className="mt-2 space-y-1">
              {Object.entries(value).map(([subKey, subValue]) =>
                renderItem(subKey, subValue),
              )}
            </ul>
          </div>
        );
      }

      return (
        <li key={key} className="text-foreground text-sm">
          <strong className="text-white">{key}:</strong> {value}
        </li>
      );
    };

    return (
      <div>
        <ul className="space-y-2">
          {Object.entries(data).map(([key, value]) => renderItem(key, value))}
        </ul>
      </div>
    );
  };

  const CollapsibleCodeBlock = ({ language, content, previewLines = 10 }) => {
    const [isExpanded, setIsExpanded] = useState(false);

    const toggleExpand = () => setIsExpanded((prev) => !prev);

    const formattedContent =
      language === "json"
        ? (() => {
            try {
              return JSON.stringify(JSON.parse(content), null, 2);
            } catch (error) {
              console.error("Invalid JSON content:", error);
              return content;
            }
          })()
        : content;

    const lineCount = formattedContent.split("\n").length;

    return (
      <div className="mt-4">
        <pre className="bg-card overflow-x-auto rounded-lg p-4">
          <code className={`language-${language}`}>
            {formattedContent
              .split("\n")
              .slice(0, isExpanded ? undefined : previewLines)
              .join("\n")}
          </code>
        </pre>
        {lineCount > previewLines && (
          <Button
            variant="ghost"
            onClick={toggleExpand}
            className="border-border bg-secondary text-foreground hover:bg-muted mt-2 rounded border px-3 py-1 text-sm"
          >
            {isExpanded ? "Show Less" : "Show More"}
          </Button>
        )}
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <Modal
      className="ordering-catalog-surface"
      isOpen={isOpen}
      onClose={handleClose}
      title="Integration Logs"
      size="xl"
    >
      <div className="space-y-6">
        {/* Log Type Legend */}
        <div className="flex items-center justify-end space-x-6">
          <div className="flex items-center space-x-2">
            <div className="bg-primary h-3 w-3 rounded-full"></div>
            <span className="text-foreground text-sm">Info</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="bg-warning h-3 w-3 rounded-full"></div>
            <span className="text-foreground text-sm">Warning</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="bg-destructive h-3 w-3 rounded-full"></div>
            <span className="text-foreground text-sm">Critical</span>
          </div>
        </div>

        {/* Related Group Event Filter */}
        {relatedGroupEvent && (
          <div className="border-primary/30 bg-primary/20 flex items-center justify-between rounded-lg border p-3">
            <span className="text-primary text-sm">
              Group: {relatedGroupEvent}
            </span>
            <Button
              variant="ghost"
              onClick={clearRelatedEvents}
              className="text-primary hover:text-primary"
            >
              <Icon name="X" className="h-4 w-4" />
            </Button>
          </div>
        )}

        {/* Logs Table */}
        {isLoading ? (
          <LoadingState />
        ) : error ? (
          <ErrorState
            title="Failed to load logs"
            message={
              error.message || "Something went wrong while loading logs."
            }
          />
        ) : logs.length === 0 ? (
          <div className="py-8 text-center">
            <Icon
              name="FileText"
              className="text-muted-foreground mx-auto mb-4 h-12 w-12"
            />
            <p className="text-muted-foreground">No logs found</p>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-700">
                <thead className="bg-card">
                  <tr>
                    <th className="text-foreground px-6 py-3 text-left text-xs font-medium tracking-wider uppercase">
                      Type
                    </th>
                    <th className="text-foreground px-6 py-3 text-left text-xs font-medium tracking-wider uppercase">
                      Event
                    </th>
                    <th className="text-foreground px-6 py-3 text-left text-xs font-medium tracking-wider uppercase">
                      Created At
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-background divide-y divide-gray-700">
                  {logs.map((log) => (
                    <tr
                      key={log.id}
                      onClick={() => handleLogSelect(log)}
                      className="hover:bg-card cursor-pointer"
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div
                            className="mr-2 h-3 w-3 rounded-full"
                            style={{
                              backgroundColor: getLogTypeColor(log.type),
                            }}
                          ></div>
                          <span className="text-foreground text-sm">
                            {getLogTypeLabel(log.type)}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-white">
                          {log.event || "Unknown"}
                        </div>
                      </td>
                      <td className="text-foreground px-6 py-4 text-sm whitespace-nowrap">
                        {formatDate(log.created_at)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.total_pages > 1 && (
              <Pagination
                currentPage={pagination.current_page || 1}
                totalPages={pagination.total_pages || 1}
                onPageChange={handlePageChange}
                totalItems={pagination.total || 0}
                pageSize={pageSize}
              />
            )}
          </div>
        )}

        {/* Log Details Modal */}
        {showLogDetails && selectedLog && (
          <Modal
            className="ordering-catalog-surface"
            isOpen={showLogDetails}
            onClose={() => setShowLogDetails(false)}
            title="Log Details"
            size="lg"
          >
            {isLoadingDetail ? (
              <LoadingState />
            ) : logDetail ? (
              <div className="space-y-6">
                {/* Basic Info */}
                <div className="bg-card rounded-lg p-4">
                  <h3 className="mb-4 text-lg font-medium text-white">
                    Log Information
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <span className="text-muted-foreground text-sm">
                        Event:
                      </span>
                      <p className="text-white">{logDetail.event}</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground text-sm">
                        Type:
                      </span>
                      <p className="text-white">
                        {getLogTypeLabel(logDetail.type)}
                      </p>
                    </div>
                    <div>
                      <span className="text-muted-foreground text-sm">
                        Created At:
                      </span>
                      <p className="text-white">
                        {formatDate(logDetail.created_at)}
                      </p>
                    </div>
                    <div>
                      <span className="text-muted-foreground text-sm">
                        Group Key:
                      </span>
                      <p className="text-white">
                        {logDetail.group_key || "N/A"}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Related Events */}
                {logDetail.group_key && (
                  <div className="border-primary/30 bg-primary/20 rounded-lg border p-4">
                    <Button
                      variant="ghost"
                      onClick={() =>
                        handleViewRelatedEvents(logDetail.group_key)
                      }
                      className="text-primary hover:text-primary text-sm underline"
                    >
                      View related group events
                    </Button>
                  </div>
                )}

                {/* Exception */}
                {logDetail.exception && (
                  <div className="bg-card rounded-lg p-4">
                    <h4 className="mb-3 text-lg font-medium text-white">
                      Exception
                    </h4>
                    {renderDynamicData(logDetail.exception)}
                  </div>
                )}

                {/* Request */}
                {logDetail.request && (
                  <div className="bg-card rounded-lg p-4">
                    <h4 className="mb-3 text-lg font-medium text-white">
                      Request
                    </h4>
                    {renderDynamicData(logDetail.request, [
                      "payload",
                      "payload_type",
                    ])}
                    {logDetail.request.payload &&
                      logDetail.request.payload_type && (
                        <CollapsibleCodeBlock
                          language={logDetail.request.payload_type}
                          content={logDetail.request.payload}
                          previewLines={10}
                        />
                      )}
                  </div>
                )}

                {/* Response */}
                {logDetail.response && (
                  <div className="bg-card rounded-lg p-4">
                    <h4 className="mb-3 text-lg font-medium text-white">
                      Response
                    </h4>
                    {renderDynamicData(logDetail.response, [
                      "content",
                      "content_type",
                    ])}
                    {logDetail.response.content &&
                      logDetail.response.content_type && (
                        <CollapsibleCodeBlock
                          language={logDetail.response.content_type}
                          content={logDetail.response.content}
                          previewLines={10}
                        />
                      )}
                  </div>
                )}
              </div>
            ) : (
              <ErrorState
                title="Failed to load log details"
                message="Could not load the selected log details."
              />
            )}
          </Modal>
        )}
      </div>
    </Modal>
  );
};

export default IntegrationLogs;
